import { SetStateAction, useEffect, useState } from "react";
import { ModalWithButtons } from "./modal";
import { BlackButton, GreyButton } from "../button";
import { useTranslation } from "react-i18next";
import { NeutralColor } from "node-library/dist/colors";
import { FrontendTestResult } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import { CommonSwitch, CustomTextInput } from "../inputs";
import { Severity } from "node-library/dist/constants";
import { GreenTickIcon } from "../toast/successIcons";
import { useSnackbar } from "../toast/showToastMessage";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<SetStateAction<boolean>>;
	recordedResult: FrontendTestResult;
	proceedAction: (updatedResult: FrontendTestResult) => void;
}

export default function WorkflowResultDeviationModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [deviation, setDeviation] = useState<boolean>(
		props.recordedResult.hasDeviation
	);
	const [result, setResult] = useState<string>(
		props.recordedResult.value || ""
	);
	const [comments, setComments] = useState<string>(
		props.recordedResult.comments || ""
	);

	const [resultErrorMsg, setResultErrorMsg] = useState<string>("");
	const [commentErrorMsg, setCommentErrorMsg] = useState<string>("");

	function handleSubmitDeviation() {
		//validation
		let hasError: boolean = false;
		if (deviation) {
			if (!result) {
				setResultErrorMsg(translation("MissingInformation"));
				hasError = true;
			}
			if (!comments) {
				setCommentErrorMsg(translation("MissingInformation"));
				hasError = true;
			}
		}
		if (hasError) {
			return;
		}
		const updatedResult: FrontendTestResult = deviation
			? {
					...props.recordedResult,
					hasDeviation: deviation,
					value: result,
					comments,
			  }
			: {
					...props.recordedResult,
					hasDeviation: deviation,
					comments: undefined,
			  };
		props.proceedAction(updatedResult);
		openSnackbar(
			translation("ResultUpdated"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />
		);
		props.setOpen(false);
	}

	useEffect(() => {
		if (props.open) {
			setDeviation(props.recordedResult.hasDeviation);
			setComments(props.recordedResult.comments || "");
		}
	}, [props.open]);

	const deviationSwitch: JSX.Element = (
		<div className="common-card-header">
			<div className="typography-body-01">
				{translation("DeviateFromResult")}
			</div>
			<CommonSwitch
				id={"deviation-switch"}
				checked={deviation}
				setChecked={setDeviation}
			/>
		</div>
	);
	const resultBox: JSX.Element = (
		<CustomTextInput
			id="deviation-result"
			value={result}
			setValue={(value) => {
				setResult(value);
			}}
			label={translation("Result")}
			errorMsg={resultErrorMsg}
			setErrorMsg={setResultErrorMsg}
		/>
	);
	const commentsBox: JSX.Element = (
		<CustomTextInput
			id="deviation-comments"
			value={comments}
			setValue={(value) => {
				setComments(value);
			}}
			label={translation("Reason")}
			isTextArea
			errorMsg={commentErrorMsg}
			setErrorMsg={setCommentErrorMsg}
		/>
	);

	const modal: React.ReactNode = (
		<ModalWithButtons
			headerText={translation("ResultDeviation")}
			// headerDescription={translation("UploadfilesDescription")}
			open={props.open}
			setOpen={props.setOpen}
			modalWidth={500}
			buttons={[
				<GreyButton
					key="delete-sample-cancel-button"
					id="delete-sample-cancel-button"
					children={translation("Cancel")}
					onClick={() => {
						props.setOpen(false);
					}}
				></GreyButton>,
				<BlackButton
					key="delete-sample-proceed-button"
					id="delete-sample-proceed-button"
					children={translation("Confirm")}
					onClick={() => {
						handleSubmitDeviation();
					}}
				></BlackButton>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "5px",
				}}
			>
				{deviationSwitch}
				{deviation && (
					<div>
						{resultBox}
						{commentsBox}
					</div>
				)}
			</div>
		</ModalWithButtons>
	);

	return modal;
}
